import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import DronesPage from "./pages/DronesPage";
import MissionsPage from "./pages/MissionsPage";
import NewMissionPage from "./pages/NewMissionPage";
import MissionDetailPage from "./pages/MissionDetailPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import AdminPage from "./pages/AdminPage";
import FAQPage from "./pages/FAQPage";
import { BlogPage, BlogPostPage } from "./pages/BlogPage";
import CountryPage from "./pages/CountryPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import ContactPage from "./pages/ContactPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import ConfidentialitePage from "./pages/ConfidentialitePage";
import CookiesPage from "./pages/CookiesPage";
import CGUPage from "./pages/CGUPage";
import CGVPage from "./pages/CGVPage";
import { CookieBanner } from "./components/CookieBanner";
import InstallPrompt from "./components/pwa/InstallPrompt";
import PWAUpdateBanner from "./components/pwa/PWAUpdateBanner";
import SupportWidget from "./components/support/SupportWidget";
import { initAnalytics, trackEvent } from "./lib/analytics";
import "@/App.css";

/**
 * Sentinel that scrolls the window to top on every route change.
 *
 * React Router preserves the previous scroll position by default which is
 * confusing when users click a deep link in the homepage footer (countries
 * list, blog links, …) and land mid-page on the target route. Skipped when
 * the URL carries a hash so in-page anchors keep working.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname, hash]);
  return null;
}

/**
 * Fires a `page_view` product event on every route change and a
 * `dashboard_viewed` alias when landing on /dashboard (so the funnel
 * step "A vu le dashboard" can be counted without inspecting paths).
 */
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    trackEvent('page_view');
    if (pathname === '/dashboard') trackEvent('dashboard_viewed');
    if (pathname === '/subscription') trackEvent('pricing_viewed');
  }, [pathname]);
  return null;
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const { i18n } = useTranslation();

  // Sync <html lang="..."> with active i18n language for SEO + accessibility
  useEffect(() => {
    const updateHtmlLang = (lng) => {
      if (lng && typeof lng === 'string') {
        document.documentElement.lang = lng.split('-')[0];
      }
    };
    updateHtmlLang(i18n.language);
    i18n.on('languageChanged', updateHtmlLang);
    return () => i18n.off('languageChanged', updateHtmlLang);
  }, [i18n]);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <InstallPrompt />
          <PWAUpdateBanner />
          <SupportWidget />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/fr" element={<LandingPage />} />
            <Route path="/en" element={<LandingPage />} />
            <Route path="/de" element={<LandingPage />} />
            <Route path="/it" element={<LandingPage />} />
            <Route path="/es" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/fr/blog" element={<BlogPage />} />
            <Route path="/en/blog" element={<BlogPage />} />
            <Route path="/de/blog" element={<BlogPage />} />
            <Route path="/it/blog" element={<BlogPage />} />
            <Route path="/es/blog" element={<BlogPage />} />
            <Route path="/fr/blog/:slug" element={<BlogPostPage />} />
            <Route path="/en/blog/:slug" element={<BlogPostPage />} />
            <Route path="/de/blog/:slug" element={<BlogPostPage />} />
            <Route path="/it/blog/:slug" element={<BlogPostPage />} />
            <Route path="/es/blog/:slug" element={<BlogPostPage />} />

            {/* Country regulation pages (SEO long-tail) */}
            <Route path="/regulations/:country" element={<CountryPage />} />
            <Route path="/fr/regulations/:country" element={<CountryPage />} />
            <Route path="/en/regulations/:country" element={<CountryPage />} />
            <Route path="/de/regulations/:country" element={<CountryPage />} />
            <Route path="/it/regulations/:country" element={<CountryPage />} />
            <Route path="/es/regulations/:country" element={<CountryPage />} />

            {/* Flagship SEO landing pages (FR-only commercial intent) */}
            <Route path="/preparation-vol-drone" element={<SeoLandingPage />} />
            <Route path="/mission-drone" element={<SeoLandingPage />} />
            <Route path="/reglementation-drone-andorre" element={<SeoLandingPage />} />
            <Route path="/reglamentacion-drones-andorra" element={<SeoLandingPage />} />
            <Route path="/drone-regulations-andorra" element={<SeoLandingPage />} />
            <Route path="/reglamentacio-drons-andorra" element={<SeoLandingPage />} />
            <Route path="/fr/preparation-vol-drone" element={<SeoLandingPage />} />
            <Route path="/fr/mission-drone" element={<SeoLandingPage />} />
            <Route path="/fr/reglementation-drone-andorre" element={<SeoLandingPage />} />
            <Route path="/es/reglamentacion-drones-andorra" element={<SeoLandingPage />} />
            <Route path="/en/drone-regulations-andorra" element={<SeoLandingPage />} />
            <Route path="/ca/reglamentacio-drons-andorra" element={<SeoLandingPage />} />

            {/* Contact page */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/fr/contact" element={<ContactPage />} />
            <Route path="/en/contact" element={<ContactPage />} />
            <Route path="/de/contact" element={<ContactPage />} />
            <Route path="/it/contact" element={<ContactPage />} />
            <Route path="/es/contact" element={<ContactPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/confidentialite" element={<ConfidentialitePage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/cgu" element={<CGUPage />} />
            <Route path="/cgv" element={<CGVPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/drones" element={<ProtectedRoute><DronesPage /></ProtectedRoute>} />
            <Route path="/missions" element={<ProtectedRoute><MissionsPage /></ProtectedRoute>} />
            <Route path="/missions/new" element={<ProtectedRoute><NewMissionPage /></ProtectedRoute>} />
            <Route path="/missions/:id" element={<ProtectedRoute><MissionDetailPage /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
